import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import apiClient from "../../../core/config/api";
import { extractErrorMessage } from "../store/hubspotSlice";
import type { HubspotBulkJobStatus, SalesforceBulkJob, SalesforceList } from "../types";
import { JobResultCounts, JobsPagination } from "./HubspotListVerifier";

// The table keeps polling while any job is in one of these.
const LIVE_STATUSES: HubspotBulkJobStatus[] = ["pending", "fetching", "queued", "writing_back"];

const STATUS_COPY: Record<HubspotBulkJobStatus, string> = {
  pending: "Starting",
  fetching: "Reading the list",
  queued: "Verifying",
  writing_back: "Writing to Salesforce",
  completed: "Completed",
  failed: "Failed",
};

const statusLabel = (job: SalesforceBulkJob) =>
  job.status === "queued" && job.contact_count > 0
    ? `${STATUS_COPY.queued} ${job.verified_count.toLocaleString()} / ${job.contact_count.toLocaleString()}`
    : STATUS_COPY[job.status];

const POLL_INTERVAL_MS = 3000;
const JOBS_PAGE_SIZE = 10;

/**
 * "Verify a Salesforce list": pick a Lead or Contact list view, and the
 * backend verifies every email in it and writes the result onto each
 * record's RS Email Verifier fields.
 */
const SalesforceListVerifier: React.FC = () => {
  const [lists, setLists] = useState<SalesforceList[]>([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [listsLoaded, setListsLoaded] = useState(false);
  const [jobs, setJobs] = useState<SalesforceBulkJob[]>([]);
  const [selectedListId, setSelectedListId] = useState("");
  const [listSearch, setListSearch] = useState("");
  const [jobsPage, setJobsPage] = useState(1);
  const [creating, setCreating] = useState(false);

  // The endpoint's maximum, and no total comes back - the table pages
  // through these client-side.
  const loadJobs = useCallback(async () => {
    try {
      const { data } = await apiClient.get<SalesforceBulkJob[]>("/salesforce/bulk-jobs", {
        params: { limit: 100 },
      });
      setJobs(Array.isArray(data) ? data : []);
    } catch {
      // Nothing to show yet; the table appears once a job exists.
    }
  }, []);

  // Refreshes only the newest page. Unseen jobs (e.g. started from another
  // tab) are newer than everything loaded, so they go on top.
  const pollJobs = useCallback(async () => {
    try {
      const { data } = await apiClient.get<SalesforceBulkJob[]>("/salesforce/bulk-jobs", {
        params: { limit: JOBS_PAGE_SIZE },
      });
      if (!Array.isArray(data)) return;
      setJobs((current) => {
        const fresh = new Map(data.map((job) => [job.id, job]));
        const known = new Set(current.map((job) => job.id));
        return [
          ...data.filter((job) => !known.has(job.id)),
          ...current.map((job) => fresh.get(job.id) ?? job),
        ];
      });
    } catch {
      // The table keeps what it last showed; the next poll tries again.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadLists = async () => {
      try {
        const { data } = await apiClient.get<SalesforceList[]>("/salesforce/lists");
        if (!cancelled) {
          setLists(Array.isArray(data) ? data : []);
          setListsLoaded(true);
        }
      } catch (err: unknown) {
        if (!cancelled) toast.error(extractErrorMessage(err, "Could not load your Salesforce lists."));
      } finally {
        if (!cancelled) setListsLoading(false);
      }
    };
    loadLists();
    loadJobs();
    return () => {
      cancelled = true;
    };
  }, [loadJobs]);

  // Every list view arrives in one response (the backend follows Salesforce's
  // paging), so the search filters here rather than asking Salesforce again.
  const filteredLists = useMemo(() => {
    const term = listSearch.trim().toLowerCase();
    return term ? lists.filter((item) => item.name.toLowerCase().includes(term)) : lists;
  }, [lists, listSearch]);

  // A search that hides the chosen list would otherwise leave it selected with
  // nothing showing, and Verify list would start a list the user cannot see.
  useEffect(() => {
    if (selectedListId && !filteredLists.some((item) => item.id === selectedListId)) {
      setSelectedListId("");
    }
  }, [filteredLists, selectedListId]);

  // Only while something is in flight: a table of finished jobs never changes.
  const hasLiveJob = jobs.some((job) => LIVE_STATUSES.includes(job.status));
  useEffect(() => {
    if (!hasLiveJob) return;
    const timer = window.setInterval(pollJobs, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [hasLiveJob, pollJobs]);

  const selectedList = lists.find((item) => item.id === selectedListId);
  const leadLists = filteredLists.filter((item) => item.object_type === "Lead");
  const contactLists = filteredLists.filter((item) => item.object_type === "Contact");

  const jobsTotalPages = Math.max(1, Math.ceil(jobs.length / JOBS_PAGE_SIZE));
  const jobsPageShown = Math.min(jobsPage, jobsTotalPages);
  const pagedJobs = jobs.slice((jobsPageShown - 1) * JOBS_PAGE_SIZE, jobsPageShown * JOBS_PAGE_SIZE);

  const handleVerify = async () => {
    if (!selectedList) return;
    setCreating(true);
    try {
      const { data } = await apiClient.post<SalesforceBulkJob>("/salesforce/bulk-jobs", {
        list_id: selectedList.id,
        list_name: selectedList.name,
        object_type: selectedList.object_type,
      });
      setSelectedListId("");
      // Prepended rather than refetched: the table is newest-first and the new
      // row is already the newest. A poll may have brought it in first.
      setJobs((current) => [data, ...current.filter((job) => job.id !== data.id)]);
      setJobsPage(1);
      toast.success(
        `Verifying "${selectedList.name}". Results are written onto the records as they finish.`
      );
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err, "Could not start the verification. Please try again."));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="hubspot-lists">
      <div className="hubspot-log-head">Verify a Salesforce list</div>

      <p className="hubspot-lists-desc">
        Pick a Lead or Contact list view and ReachStream verifies every email in it, then writes the
        result onto each record&apos;s <strong>RS Email Verifier Status</strong> field.
      </p>

      <div className="hubspot-lists-picker">
        <input
          type="search"
          className="hubspot-lists-search"
          value={listSearch}
          onChange={(event) => setListSearch(event.target.value)}
          placeholder="Search lists by name..."
          disabled={creating}
          aria-label="Search Salesforce list views"
        />

        <select
          className="hubspot-lists-select"
          value={selectedListId}
          onChange={(event) => setSelectedListId(event.target.value)}
          disabled={listsLoading || creating || filteredLists.length === 0}
          aria-label="Salesforce list view"
        >
          <option value="">
            {listsLoading
              ? "Loading your lists..."
              : filteredLists.length === 0
                ? listSearch.trim()
                  ? "No lists match that search"
                  : "No Lead or Contact list views found"
                : "Choose a list..."}
          </option>
          {leadLists.length > 0 && (
            <optgroup label="Leads">
              {leadLists.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </optgroup>
          )}
          {contactLists.length > 0 && (
            <optgroup label="Contacts">
              {contactLists.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>

        <button
          type="button"
          className="connect-btn"
          onClick={handleVerify}
          disabled={!selectedListId || creating}
        >
          {creating ? "Starting..." : "Verify list"}
        </button>
      </div>

      {selectedList && (
        <p className="hubspot-lists-hint">
          One credit is charged per email address - records without one, and malformed addresses,
          are free.
        </p>
      )}

      {listsLoaded && lists.length === 0 && (
        <p className="hubspot-lists-hint">
          Create a list view in Salesforce on the <strong>Leads</strong> or <strong>Contacts</strong>{" "}
          tab, then come back here.
        </p>
      )}

      {jobs.length > 0 && (
        <table className="hubspot-log-table">
          <thead>
            <tr>
              <th>List</th>
              <th>Status</th>
              <th>Records</th>
              <th>Results</th>
              <th>Written back</th>
              <th>Started</th>
            </tr>
          </thead>
          <tbody>
            {pagedJobs.map((job) => (
              <tr key={job.id}>
                <td>
                  {job.list_view_name || `List ${job.list_view_id}`}
                  <div className="muted">{job.object_type === "Lead" ? "Leads" : "Contacts"}</div>
                </td>
                <td>
                  <span className={`verdict-badge ${job.status}`}>{statusLabel(job)}</span>
                  {/* On a completed job this says how many records Salesforce refused. */}
                  {(job.status === "failed" || job.status === "completed") && job.error_message && (
                    <div className="hubspot-lists-error">{job.error_message}</div>
                  )}
                </td>
                <td className="muted">{job.contact_count.toLocaleString()}</td>
                <td>
                  <JobResultCounts job={job} />
                </td>
                <td className="muted">
                  {job.written_count.toLocaleString()}
                  {job.verified_count > 0 ? ` / ${job.verified_count.toLocaleString()}` : ""}
                </td>
                <td className="muted">{new Date(job.created_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <JobsPagination page={jobsPageShown} totalPages={jobsTotalPages} onPageChange={setJobsPage} />
    </div>
  );
};

export default SalesforceListVerifier;
